import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@fikasio/react-ui-components';
import { DueDatePreset } from 'components/filters/dueDatePreset';
import { UNASSIGNED_FILTER_VALUE } from 'components/tasks/utils';
import assigneeStyles from 'components/tasks/TasksFilters/TasksFilters.module.css';
import FilterButton from 'components/UI/FilterButton';
import filterButtonStyles from 'components/UI/FilterButton.module.css';
import FilterModal from 'components/UI/FilterModal';
import FilterSelect from 'components/UI/FilterSelect';
import UserAvatar from 'components/UI/UserAvatar';
import { useAuth } from 'services/login/hooks';
import { useCurrentOrganizationUsersQuery } from 'services/organizations/api';
import { assigneeDisplayName, withCurrentAssignableUser } from 'services/users/assignable';

export interface ObjectivesFilterDraft {
  assignees: string[];
  completion: 'ALL' | 'COMPLETE' | 'INCOMPLETE' | 'FAILED' | 'CANCELLED' | 'ARCHIVED';
  dueDate: DueDatePreset;
}

export const DEFAULT_OBJECTIVES_FILTER_DRAFT: ObjectivesFilterDraft = {
  assignees: [],
  completion: 'INCOMPLETE',
  dueDate: 'ALL',
};

interface ObjectivesFiltersProps {
  onApply: (value: ObjectivesFilterDraft) => void;
  showAssigneeFilter?: boolean;
  showCompletionFilter?: boolean;
  showDueDateFilter?: boolean;
  value: ObjectivesFilterDraft;
}

const countActiveFilters = (
  value: ObjectivesFilterDraft,
  options: {
    showAssigneeFilter?: boolean;
    showCompletionFilter?: boolean;
    showDueDateFilter?: boolean;
  },
) => {
  let count = 0;
  if (options.showCompletionFilter && value.completion !== 'INCOMPLETE') {
    count += 1;
  }
  if (options.showDueDateFilter && value.dueDate !== 'ALL') {
    count += 1;
  }
  if (options.showAssigneeFilter && value.assignees.length > 0) {
    count += 1;
  }
  return count;
};

const ObjectivesFilters = ({
  onApply,
  showAssigneeFilter,
  showCompletionFilter,
  showDueDateFilter,
  value,
}: ObjectivesFiltersProps) => {
  const { t } = useTranslation();
  const includeAssignees = showAssigneeFilter ?? !!(showCompletionFilter || showDueDateFilter);
  const hasFilters = includeAssignees || !!showCompletionFilter || !!showDueDateFilter;
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<ObjectivesFilterDraft>(value);
  const { user: currentUser } = useAuth();
  const { data: assignableUsers } = useCurrentOrganizationUsersQuery();
  const users = withCurrentAssignableUser(assignableUsers, currentUser);

  const toggleAssignee = (assigneeId: string) => {
    setDraft(current => ({
      ...current,
      assignees: current.assignees.includes(assigneeId)
        ? current.assignees.filter(id => id !== assigneeId)
        : [...current.assignees, assigneeId],
    }));
  };

  if (!hasFilters) {
    return null;
  }

  return (
    <div className={filterButtonStyles.anchor}>
      <FilterButton
        activeCount={countActiveFilters(value, {
          showAssigneeFilter: includeAssignees,
          showCompletionFilter,
          showDueDateFilter,
        })}
        isOpen={isOpen}
        onClick={() => {
          setDraft(value);
          setIsOpen(true);
        }}
      />
      <FilterModal
        onApply={() => {
          onApply(draft);
          setIsOpen(false);
        }}
        onClear={() => {
          setDraft(DEFAULT_OBJECTIVES_FILTER_DRAFT);
          onApply(DEFAULT_OBJECTIVES_FILTER_DRAFT);
        }}
        onHide={() => setIsOpen(false)}
        show={isOpen}
      >
        {
          showCompletionFilter && (
            <FilterSelect
              label={t('status')}
              onChange={completion => setDraft(current => ({
                ...current,
                completion: completion as ObjectivesFilterDraft['completion'],
              }))}
              options={[
                { color: '#607d8b', icon: 'square', label: t('incompleteObjectives'), value: 'INCOMPLETE' },
                { color: '#4caf50', icon: 'check', label: t('completeObjectives'), value: 'COMPLETE' },
                { color: '#c62828', icon: 'times', label: t('failedObjectives'), value: 'FAILED' },
                { color: '#9e9e9e', icon: 'times', label: t('cancelledObjectives'), value: 'CANCELLED' },
                { color: '#5c6bc0', icon: 'archive', label: t('archivedObjectives'), value: 'ARCHIVED' },
                { color: '#607d8b', icon: 'list', label: t('allObjectives'), value: 'ALL' },
              ]}
              value={draft.completion}
            />
          )
        }
        {
          showDueDateFilter && (
            <FilterSelect
              label={t('dueDate')}
              onChange={dueDate => setDraft(current => ({
                ...current,
                dueDate: dueDate as DueDatePreset,
              }))}
              options={[
                { color: '#607d8b', icon: 'calendar-alt', label: t('anyDueDate'), value: 'ALL' },
                { color: '#2e7d32', icon: 'calendar-alt', label: t('dueToday'), value: 'FOR_TODAY' },
                { color: '#f5a623', icon: 'calendar-alt', label: t('dueThisWeek'), value: 'FOR_THISWEEK' },
                { color: '#c62828', icon: 'triangle-exclamation', label: t('overdue'), value: 'OVERDUE' },
                { color: '#cecece', icon: 'square', label: t('noDueDate'), value: 'NONE' },
              ]}
              value={draft.dueDate}
            />
          )
        }
        {
          includeAssignees && (
            <div>
              <div className="fw-semibold mb-2">{t('assignees')}</div>
              <div className={assigneeStyles.assigneeList}>
                <label className={assigneeStyles.assigneeOption}>
                  <input
                    checked={draft.assignees.includes(UNASSIGNED_FILTER_VALUE)}
                    onChange={() => toggleAssignee(UNASSIGNED_FILTER_VALUE)}
                    type="checkbox"
                  />
                  <Icon
                    name="user"
                    style={{
                      color: '#cecece',
                      flexShrink: 0,
                      height: 24,
                      width: 24,
                    }}
                  />
                  <span className={assigneeStyles.assigneeName}>{t('unassigned')}</span>
                </label>
                {
                  users.map(user => (
                    <label
                      className={assigneeStyles.assigneeOption}
                      key={user.id}
                    >
                      <input
                        checked={draft.assignees.includes(user.id)}
                        onChange={() => toggleAssignee(user.id)}
                        type="checkbox"
                      />
                      <UserAvatar
                        alt=""
                        hasProfilePicture={user.hasProfilePicture}
                        modifiedAt={user.modifiedAt}
                        size={24}
                        userId={user.id}
                      />
                      <span className={assigneeStyles.assigneeName}>
                        {assigneeDisplayName(user)}
                      </span>
                    </label>
                  ))
                }
              </div>
            </div>
          )
        }
      </FilterModal>
    </div>
  );
};

export default ObjectivesFilters;
